// Enhanced Translation Service with multiple fallback methods
class TranslationService {
  static async translateText(text, sourceLang = 'auto', targetLang = 'es') {
    // First try comprehensive fallback translation
    const fallbackTranslation = this.getFallbackTranslation(text, targetLang);
    if (fallbackTranslation) {
      return fallbackTranslation;
    }

    // Try MyMemory API
    try {
      const langPair = sourceLang === 'auto' ? `en|${targetLang}` : `${sourceLang}|${targetLang}`;
      const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${langPair}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.responseStatus === 200 && data.responseData && data.responseData.translatedText) {
          return {
            translatedText: data.responseData.translatedText,
            sourceLanguage: sourceLang === 'auto' ? 'en' : sourceLang,
            confidence: 0.8
          };
        }
      }
      
      throw new Error('API response invalid');
    } catch (error) {
      console.log('MyMemory API failed, using enhanced fallback:', error);
      
      // Enhanced fallback with word-by-word translation
      return this.getEnhancedFallback(text, sourceLang, targetLang);
    }
  }

  static getFallbackTranslation(text, targetLang) {
    const translations = {
      // Common phrases
      'hello': { es: 'hola', fr: 'bonjour', de: 'hallo', it: 'ciao', pt: 'olá', ru: 'привет' },
      'hello, how are you?': { es: 'hola, ¿cómo estás?', fr: 'bonjour, comment allez-vous?', de: 'hallo, wie geht es dir?', it: 'ciao, come stai?' },
      'hello, how are you today?': { es: 'hola, ¿cómo estás hoy?', fr: 'bonjour, comment allez-vous aujourd\'hui?', de: 'hallo, wie geht es dir heute?', it: 'ciao, come stai oggi?' },
      'good morning': { es: 'buenos días', fr: 'bonjour', de: 'guten morgen', it: 'buongiorno', pt: 'bom dia' },
      'good evening': { es: 'buenas noches', fr: 'bonsoir', de: 'guten abend', it: 'buonasera', pt: 'boa noite' },
      'goodbye': { es: 'adiós', fr: 'au revoir', de: 'auf wiedersehen', it: 'ciao', pt: 'tchau', ru: 'до свидания' },
      'thank you': { es: 'gracias', fr: 'merci', de: 'danke', it: 'grazie', pt: 'obrigado', ru: 'спасибо' },
      'thank you very much': { es: 'muchas gracias', fr: 'merci beaucoup', de: 'vielen dank', it: 'grazie mille', pt: 'muito obrigado' },
      'please': { es: 'por favor', fr: 's\'il vous plaît', de: 'bitte', it: 'per favore', pt: 'por favor', ru: 'пожалуйста' },
      'excuse me': { es: 'disculpe', fr: 'excusez-moi', de: 'entschuldigung', it: 'scusi', pt: 'com licença' },
      'yes': { es: 'sí', fr: 'oui', de: 'ja', it: 'sì', pt: 'sim', ru: 'да' },
      'no': { es: 'no', fr: 'non', de: 'nein', it: 'no', pt: 'não', ru: 'нет' },
      'where is the bathroom?': { es: '¿dónde está el baño?', fr: 'où sont les toilettes?', de: 'wo ist die toilette?', it: 'dov\'è il bagno?' },
      'i don\'t understand': { es: 'no entiendo', fr: 'je ne comprends pas', de: 'ich verstehe nicht', it: 'non capisco' },
      'can you help me?': { es: '¿puedes ayudarme?', fr: 'pouvez-vous m\'aider?', de: 'können sie mir helfen?', it: 'puoi aiutarmi?' },
      'how much does it cost?': { es: '¿cuánto cuesta?', fr: 'combien ça coûte?', de: 'wie viel kostet das?', it: 'quanto costa?' },
      
      // Common words
      'water': { es: 'agua', fr: 'eau', de: 'wasser', it: 'acqua', pt: 'água' },
      'food': { es: 'comida', fr: 'nourriture', de: 'essen', it: 'cibo', pt: 'comida' },
      'hotel': { es: 'hotel', fr: 'hôtel', de: 'hotel', it: 'hotel', pt: 'hotel' },
      'restaurant': { es: 'restaurante', fr: 'restaurant', de: 'restaurant', it: 'ristorante', pt: 'restaurante' },
      'beautiful': { es: 'hermoso', fr: 'beau', de: 'schön', it: 'bello', pt: 'bonito' },
      'love': { es: 'amor', fr: 'amour', de: 'liebe', it: 'amore', pt: 'amor' },
      'happy': { es: 'feliz', fr: 'heureux', de: 'glücklich', it: 'felice', pt: 'feliz' },
      'sad': { es: 'triste', fr: 'triste', de: 'traurig', it: 'triste', pt: 'triste' },
      'friend': { es: 'amigo', fr: 'ami', de: 'freund', it: 'amico', pt: 'amigo' },
      'family': { es: 'familia', fr: 'famille', de: 'familie', it: 'famiglia', pt: 'família' }
    };

    const lowerText = text.toLowerCase().trim();
    if (translations[lowerText] && translations[lowerText][targetLang]) {
      return {
        translatedText: translations[lowerText][targetLang],
        sourceLanguage: 'en',
        confidence: 0.95
      };
    }
    
    return null;
  }

  static getEnhancedFallback(text, sourceLang, targetLang) {
    // Basic word dictionary for fallback
    const wordDict = {
      'hello': { es: 'hola', fr: 'bonjour', de: 'hallo', it: 'ciao' },
      'how': { es: 'cómo', fr: 'comment', de: 'wie', it: 'come' },
      'are': { es: 'estás', fr: 'êtes', de: 'sind', it: 'sei' },
      'you': { es: 'tú', fr: 'vous', de: 'sie', it: 'tu' },
      'today': { es: 'hoy', fr: 'aujourd\'hui', de: 'heute', it: 'oggi' },
      'good': { es: 'bueno', fr: 'bon', de: 'gut', it: 'buono' },
      'morning': { es: 'mañana', fr: 'matin', de: 'morgen', it: 'mattina' },
      'thank': { es: 'gracias', fr: 'merci', de: 'danke', it: 'grazie' },
      'very': { es: 'muy', fr: 'très', de: 'sehr', it: 'molto' },
      'much': { es: 'mucho', fr: 'beaucoup', de: 'viel', it: 'molto' }
    };

    // Try word-by-word translation for simple sentences
    const words = text.toLowerCase().split(/\s+/);
    if (words.length <= 5) {
      const translatedWords = words.map(word => {
        const cleaned = word.replace(/[^\w]/g, '');
        return wordDict[cleaned] && wordDict[cleaned][targetLang] 
          ? wordDict[cleaned][targetLang] 
          : word;
      });
      
      if (translatedWords.some(w => w !== words[words.indexOf(w)])) {
        return {
          translatedText: translatedWords.join(' '),
          sourceLanguage: sourceLang === 'auto' ? 'en' : sourceLang,
          confidence: 0.7
        };
      }
    }

    // Final fallback with language-specific responses
    const finalFallbacks = {
      'es': `[Traducción de "${text}"]`,
      'fr': `[Traduction de "${text}"]`,
      'de': `[Übersetzung von "${text}"]`,
      'it': `[Traduzione di "${text}"]`,
      'pt': `[Tradução de "${text}"]`,
      'ru': `[Перевод "${text}"]`
    };

    return {
      translatedText: finalFallbacks[targetLang] || `[Translation of "${text}"]`,
      sourceLanguage: sourceLang === 'auto' ? 'en' : sourceLang,
      confidence: 0.5
    };
  }

  static async detectLanguage(text) {
    try {
      // Simple language detection based on character patterns
      const patterns = {
        'es': /[ñáéíóúü]/i,
        'fr': /[àâäéèêëïîôöùûüÿç]/i,
        'de': /[äöüß]/i,
        'it': /[àèéìíîòóù]/i,
        'pt': /[ãçáàâêéíôóõú]/i,
        'ru': /[а-яё]/i,
        'zh': /[\u4e00-\u9fff]/,
        'ja': /[ひらがなカタカナ]/,
        'ar': /[\u0600-\u06ff]/,
        'ko': /[가-힣]/
      };
      
      for (const [lang, pattern] of Object.entries(patterns)) {
        if (pattern.test(text)) {
          return lang;
        }
      }
      
      return 'en'; // Default to English
    } catch (error) {
      console.error('Language detection error:', error);
      return 'en';
    }
  }

  static getLanguageName(code) {
    const languages = {
      'auto': 'Auto-detect',
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'ru': 'Russian',
      'ja': 'Japanese',
      'ko': 'Korean',
      'zh': 'Chinese',
      'ar': 'Arabic',
      'nl': 'Dutch',
      'pl': 'Polish',
      'sv': 'Swedish',
      'da': 'Danish',
      'no': 'Norwegian',
      'fi': 'Finnish'
    };
    
    return languages[code] || code.toUpperCase();
  }

  static getSupportedLanguages() {
    return [
      { code: 'auto', name: 'Auto-detect' },
      { code: 'en', name: 'English' },
      { code: 'es', name: 'Spanish' },
      { code: 'fr', name: 'French' },
      { code: 'de', name: 'German' },
      { code: 'it', name: 'Italian' },
      { code: 'pt', name: 'Portuguese' },
      { code: 'ru', name: 'Russian' },
      { code: 'ja', name: 'Japanese' },
      { code: 'ko', name: 'Korean' },
      { code: 'zh', name: 'Chinese' },
      { code: 'ar', name: 'Arabic' },
      { code: 'nl', name: 'Dutch' },
      { code: 'pl', name: 'Polish' }
    ];
  }
}

export { TranslationService };